import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import StickyCta from "@/components/site/StickyCta";
import { PILLARS, CROSS_CUTTING } from "@/data/platform";
import { INDUSTRIES } from "@/data/industries";
import { PillarIcon } from "@/components/platform/PlatformPrimitives";

function PillarCard({ pillar, i }) {
  const live = pillar.products.filter((p) => p.status === "live").length;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
    >
      <Link
        to={`/platform/${pillar.slug}`}
        data-testid={`platform-pillar-${pillar.slug}`}
        className="group h-full flex flex-col p-6 md:p-7 rounded-2xl bg-[#FDFCFB] border border-[#E8E6E1] hover:border-[#1A1A1A]/30 hover:-translate-y-1 hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.18)] transition-all min-h-[260px]"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="w-11 h-11 rounded-xl bg-[#F5F4F0] flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:text-[#FDFCFB] transition-colors">
            <PillarIcon name={pillar.icon} />
          </div>
          <span className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68]">
            / {String(i + 1).padStart(2, "0")}
          </span>
        </div>
        <h3 className="font-serif-display text-2xl md:text-[26px] text-[#1A1A1A] tracking-tight">
          {pillar.name}
        </h3>
        <p className="mt-2 font-serif-display italic text-[#1A1A1A]/75 text-sm">
          {pillar.tagline}
        </p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {pillar.products.slice(0, 4).map((p) => (
            <span
              key={p.slug}
              className="px-2.5 py-1 rounded-full bg-[#F5F4F0] text-[11px] text-[#1A1A1A]"
            >
              {p.name}
            </span>
          ))}
          {pillar.products.length > 4 && (
            <span className="px-2.5 py-1 rounded-full text-[11px] text-[#6B6A68]">
              +{pillar.products.length - 4} more
            </span>
          )}
        </div>
        <div className="mt-auto pt-6 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase font-mono-tech">
          <span className="text-[#6B6A68]">
            {live > 0 ? `${live} live · ${pillar.products.length} total` : `${pillar.products.length} products`}
          </span>
          <span className="inline-flex items-center gap-1 text-[#1A1A1A]/70">
            Explore <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

function IndustryCard({ industry, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
    >
      <Link
        to={`/industries/${industry.slug}`}
        data-testid={`industry-card-${industry.slug}`}
        className="group block rounded-2xl overflow-hidden border border-[#E8E6E1] bg-[#FDFCFB] hover:-translate-y-0.5 hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.18)] transition-all"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F5F4F0]">
          <img
            src={industry.image}
            alt={industry.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          {industry.brand && (
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#FDFCFB]/85 backdrop-blur-sm font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#1A1A1A]">
              {industry.brand}
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] tracking-tight">
            {industry.name}
          </h3>
          <p className="mt-1 text-sm text-[#6B6A68]">{industry.tagline}</p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-mono-tech text-[#1A1A1A]/70">
            View suite <ArrowUpRight className="w-3 h-3" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Platform() {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <main className="bg-[#FDFCFB] text-[#1A1A1A] min-h-screen">
      <Nav />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="caption mb-6">Platform · Overview</div>
        <h1 className="font-serif-display text-[40px] sm:text-6xl md:text-7xl tracking-tighter leading-[1.02] text-[#1A1A1A] max-w-4xl">
          One platform. <span className="italic">Every department.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-[#6B6A68] text-base md:text-lg leading-relaxed">
          Eight functional pillars and two cross-cutting layers — each with
          ready-to-use products. Use one. Use the stack. Or buy a pre-bundled
          industry suite.
        </p>
      </section>

      {/* 8 pillars */}
      <section className="py-12 md:py-16 bg-[#F5F4F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="caption mb-4">/ Functional pillars</div>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05]">
                Built around how teams actually work.
              </h2>
            </div>
            <p className="max-w-md text-[#6B6A68] md:text-right">
              Sales, marketing, HR, ops, finance and beyond — each pillar holds
              the products that team uses every day.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <PillarCard key={p.slug} pillar={p} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Cross-cutting */}
      <section className="py-16 md:py-20 bg-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="caption mb-4">/ Cross-cutting</div>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05] mb-10">
            Two layers that <span className="italic">touch everything else.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CROSS_CUTTING.map((p, i) => (
              <PillarCard key={p.slug} pillar={p} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Industry suites */}
      <section className="py-20 md:py-28 bg-[#1A1A1A] text-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="caption !text-[#FDFCFB]/50 mb-4">/ Industry suites</div>
              <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                Or start with a <span className="italic">pre-bundled suite.</span>
              </h2>
            </div>
            <p className="max-w-md text-[#FDFCFB]/65 md:text-right">
              Each suite bundles the right products across pillars for a single
              vertical. Buy once, run the whole business.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((ind, i) => (
              <IndustryCard key={ind.slug} industry={ind} i={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <StickyCta />
    </main>
  );
}
